/**
 * Pure helpers for the attribution-aware duplicate merge review UI.
 *
 * `getMatchBadges` answers "why are these two leads considered the same
 * person?" without touching the API — everything is derived from the lead
 * objects the Duplicate Leads table already holds.
 */

const MIN_PHONE_DIGITS = 7;

export const BADGE_EMAIL = 'Email match';
export const BADGE_PHONE = 'Phone match';
export const BADGE_VISITOR_ID = 'Visitor ID match';
export const BADGE_ATTRIBUTION_HISTORY = 'Attribution history';

export const CONTACT_CONFLICT_FIELDS = ['email', 'phone', 'first_name', 'last_name'];

export const CONTACT_FIELD_LABELS = {
    email: 'Email',
    phone: 'Phone',
    first_name: 'First name',
    last_name: 'Last name',
};

export function normaliseEmail(value) {
    if (typeof value !== 'string') return '';
    return value.trim().toLowerCase();
}

/**
 * Digits-only comparison. Deliberately does NOT reconcile country prefixes:
 * '+447852461505' and '07852461505' are treated as different numbers so the
 * operator, not the UI, decides whether they are the same person.
 */
export function normalisePhone(value) {
    if (value === null || value === undefined) return '';
    const digits = String(value).replace(/\D/g, '');
    return digits.length >= MIN_PHONE_DIGITS ? digits : '';
}

export function getLeadContactValue(lead, field) {
    if (!lead) return '';
    if (field === 'email' || field === 'phone') return lead?.lead_contact?.[field] || '';
    return lead?.[field] || '';
}

export function normaliseContactValue(field, value) {
    if (field === 'email') return normaliseEmail(value);
    if (field === 'phone') return normalisePhone(value);
    return typeof value === 'string' ? value.trim() : '';
}

export function getVisitorId(lead) {
    return lead?.identity?.visitor_id || '';
}

export function getAttributionHistory(lead) {
    const history = lead?.lead_source?.metadata?.attribution_history;
    return Array.isArray(history) ? history : [];
}

export function hasAttributionHistory(lead) {
    return getAttributionHistory(lead).length > 0;
}

/**
 * @returns {string[]} badge labels, in a stable display order.
 */
export function getMatchBadges(leadA, leadB) {
    if (!leadA || !leadB) return [];
    const badges = [];

    const emailA = normaliseEmail(getLeadContactValue(leadA, 'email'));
    const emailB = normaliseEmail(getLeadContactValue(leadB, 'email'));
    if (emailA && emailA === emailB) badges.push(BADGE_EMAIL);

    const phoneA = normalisePhone(getLeadContactValue(leadA, 'phone'));
    const phoneB = normalisePhone(getLeadContactValue(leadB, 'phone'));
    if (phoneA && phoneA === phoneB) badges.push(BADGE_PHONE);

    const visitorA = getVisitorId(leadA);
    const visitorB = getVisitorId(leadB);
    if (visitorA && visitorA === visitorB) badges.push(BADGE_VISITOR_ID);

    if (hasAttributionHistory(leadA) || hasAttributionHistory(leadB)) {
        badges.push(BADGE_ATTRIBUTION_HISTORY);
    }

    return badges;
}

/**
 * Contact fields where BOTH leads hold a non-empty value and the values
 * differ after normalisation. These are the only fields the merge endpoint
 * wants a `field_decisions` entry for.
 *
 * @returns {{field: string, label: string, primaryValue: string, secondaryValue: string}[]}
 */
export function getContactConflicts(primaryLead, secondaryLead) {
    if (!primaryLead || !secondaryLead) return [];
    return CONTACT_CONFLICT_FIELDS.reduce((conflicts, field) => {
        const primaryValue = getLeadContactValue(primaryLead, field);
        const secondaryValue = getLeadContactValue(secondaryLead, field);
        const normalisedPrimary = normaliseContactValue(field, primaryValue);
        const normalisedSecondary = normaliseContactValue(field, secondaryValue);
        if (!normalisedPrimary || !normalisedSecondary) return conflicts;
        if (normalisedPrimary === normalisedSecondary) return conflicts;
        conflicts.push({
            field,
            label: CONTACT_FIELD_LABELS[field],
            primaryValue,
            secondaryValue,
        });
        return conflicts;
    }, []);
}

/**
 * Finds the counterpart of `lead` inside an already-loaded list of duplicate
 * candidates, using normalised email/phone equality. Used so the Duplicate
 * Leads table can show WHY a row is flagged without an extra API call.
 *
 * Returns a match only when EXACTLY ONE other row matches by normalised
 * email or phone — with zero or several candidates there is no single
 * unambiguous counterpart, so callers get `null` rather than an arbitrary
 * (and possibly wrong) pick.
 */
export function findPairLead(lead, candidates) {
    if (!lead || !Array.isArray(candidates)) return null;
    const leadId = lead.lead_id ?? lead.id;
    const email = normaliseEmail(getLeadContactValue(lead, 'email'));
    const phone = normalisePhone(getLeadContactValue(lead, 'phone'));
    if (!email && !phone) return null;
    const matches = candidates.filter((candidate) => {
        const candidateId = candidate?.lead_id ?? candidate?.id;
        if (candidateId === undefined || candidateId === leadId) return false;
        const candidateEmail = normaliseEmail(getLeadContactValue(candidate, 'email'));
        const candidatePhone = normalisePhone(getLeadContactValue(candidate, 'phone'));
        return (!!email && email === candidateEmail) || (!!phone && phone === candidatePhone);
    });
    return matches.length === 1 ? matches[0] : null;
}
